import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { useBookingForm } from "@/hooks/useBookingForm";
import BookingProgress from "./booking/BookingProgress";
import BookingStepEvent from "./booking/BookingStepEvent";
import BookingStepContact from "./booking/BookingStepContact";
import BookingStepReview from "./booking/BookingStepReview";
import BookingSummary from "./booking/BookingSummary";

const BookingPage = () => {
  const { t } = useTranslation();
  const { l } = useLocalized();
  const navigate = useNavigate();

  const {
    packages,
    blockedDates,
    loading,
    step,
    setStep,
    submitting,
    date,
    formData,
    selectedPackage,
    fetchPackages,
    handlePackageSelect,
    handleGuestCountChange,
    handleDateSelect,
    updateField,
    handleSubmit,
    isStep1Valid,
    isStep2Valid,
  } = useBookingForm(navigate);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  const selectedPackageName = selectedPackage
    ? l(selectedPackage, "name")
    : formData.package_name;

  return (
    <div data-testid="booking-page" className="min-h-screen bg-[#F4F1DE]">
      {/* Hero Section */}
      <section
        className="pt-32 pb-16 bg-[#3D405B] relative overflow-hidden"
        data-testid="booking-hero"
      >
        <div className="absolute inset-0 opacity-10 pattern-bg" />
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <span className="inline-block bg-[#F2CC8F]/20 text-[#F2CC8F] px-4 py-2 rounded-full text-sm font-medium mb-6">
            {t("booking.hero.badge")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-['Playfair_Display']">
            {t("booking.hero.title")}
          </h1>
          <p className="text-xl text-[#F4F1DE]/80 max-w-2xl mx-auto">
            {t("booking.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Progress Steps */}
      <BookingProgress step={step} />

      {/* Form Section */}
      <section className="py-12" data-testid="booking-form-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form */}
            <div className="lg:col-span-2">
              {step === 1 && (
                <BookingStepEvent
                  packages={packages}
                  loading={loading}
                  formData={formData}
                  date={date}
                  blockedDates={blockedDates}
                  selectedPackage={selectedPackage}
                  onPackageSelect={handlePackageSelect}
                  onGuestCountChange={handleGuestCountChange}
                  onDateSelect={handleDateSelect}
                  onUpdateField={updateField}
                  onNext={() => setStep(2)}
                  isValid={isStep1Valid}
                />
              )}
              {step === 2 && (
                <BookingStepContact
                  formData={formData}
                  onUpdateField={updateField}
                  onBack={() => setStep(1)}
                  onNext={() => setStep(3)}
                  isValid={isStep2Valid}
                />
              )}
              {step === 3 && (
                <BookingStepReview
                  formData={formData}
                  submitting={submitting}
                  onBack={() => setStep(2)}
                  onSubmit={handleSubmit}
                />
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <BookingSummary formData={formData} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
